import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Select,
  Spinner,
  Center,
} from "@chakra-ui/react";
import { FaPlus, FaEdit, FaTrash } from "react-icons/fa";

const API_BASE = "/api/frota/veiculos";

const TIPOS_VEICULO = ["TOCO", "TRUCK", "3/4"];

export default function FrotaTab() {
  const [lista, setLista] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    placa: "",
    tipo: "",
    bocas: 0,
    litrosPorBoca: [],
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const capacidadeTotal = form.litrosPorBoca.reduce(
    (acc, v) => acc + (Number(v) || 0),
    0
  );

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_BASE);
      if (!res.ok) throw new Error("Erro ao carregar veículos");
      const data = await res.json();
      setLista(Array.isArray(data) ? data : []);
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
      setLista([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const n = Math.max(0, parseInt(form.bocas, 10) || 0);
    setForm((prev) => {
      const arr = [...prev.litrosPorBoca];
      while (arr.length < n) arr.push("");
      return { ...prev, litrosPorBoca: arr.slice(0, n) };
    });
  }, [form.bocas]);

  const openNew = () => {
    setEditingId(null);
    setForm({
      placa: "",
      tipo: "",
      bocas: 0,
      litrosPorBoca: [],
    });
    onOpen();
  };

  const openEdit = (row) => {
    setEditingId(row.id);
    const litros = Array.isArray(row.litros_por_boca)
      ? row.litros_por_boca.map((v) => (v != null ? String(v) : ""))
      : [];
    const bocas = row.bocas != null ? Number(row.bocas) : litros.length || 0;
    const arr = [...litros];
    while (arr.length < bocas) arr.push("");
    setForm({
      placa: row.placa ?? "",
      tipo: row.tipo ?? "",
      bocas: bocas || 0,
      litrosPorBoca: arr.slice(0, Math.max(bocas, litros.length)),
    });
    onOpen();
  };

  const setLitrosPorBocaIndex = (index, value) => {
    setForm((prev) => {
      const arr = [...prev.litrosPorBoca];
      arr[index] = value;
      return { ...prev, litrosPorBoca: arr };
    });
  };

  const handleSave = async () => {
    if (!form.placa?.trim()) {
      toast({
        title: "Placa é obrigatória",
        status: "warning",
        isClosable: true,
      });
      return;
    }
    const bocas = Math.max(0, parseInt(form.bocas, 10) || 0);
    const litrosArr = form.litrosPorBoca
      .slice(0, bocas)
      .map((v) => parseFloat(v) || 0);
    const total = litrosArr.reduce((a, b) => a + b, 0);

    setSaving(true);
    try {
      const payload = {
        placa: form.placa.trim(),
        tipo: form.tipo || null,
        bocas: bocas || null,
        litros_por_boca: litrosArr,
        capacidade_total: total,
      };
      const method = editingId ? "PUT" : "POST";
      if (editingId) payload.id = editingId;
      const res = await fetch(API_BASE, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Erro ao salvar");
      toast({
        title: "Salvo com sucesso",
        status: "success",
        isClosable: true,
      });
      onClose();
      load();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este veículo?")) return;
    try {
      const res = await fetch(`${API_BASE}?id=${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Erro ao excluir");
      toast({ title: "Excluído", status: "success", isClosable: true });
      load();
    } catch (err) {
      toast({
        title: "Erro",
        description: err.message,
        status: "error",
        isClosable: true,
      });
    }
  };

  const nBocas = Math.max(0, parseInt(form.bocas, 10) || 0);

  return (
    <Box>
      <Flex
        align="center"
        justify="space-between"
        mb={6}
        flexWrap="wrap"
        gap={4}
      >
        <Heading size="md" color="#007abe">
          Frota – Veículos
        </Heading>
        <Button
          leftIcon={<FaPlus />}
          bg="#007abe"
          color="white"
          onClick={openNew}
          _hover={{ bg: "#0066a3" }}
        >
          Novo veículo
        </Button>
      </Flex>
      <TableContainer
        bg="white"
        borderRadius="xl"
        boxShadow="md"
        overflowX="auto"
      >
        {loading ? (
          <Center py={12}>
            <Spinner size="xl" color="#007abe" />
          </Center>
        ) : (
          <Table variant="simple" size="sm">
            <Thead bg="#007abe">
              <Tr>
                <Th color="white">Placa</Th>
                <Th color="white">Tipo</Th>
                <Th color="white">Bocas</Th>
                <Th color="white">Litros por boca</Th>
                <Th color="white">Capacidade total (L)</Th>
                <Th color="white" width="120px">
                  Ações
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {lista.map((row) => (
                <Tr key={row.id}>
                  <Td fontWeight="medium">{row.placa || "–"}</Td>
                  <Td>{row.tipo || "–"}</Td>
                  <Td>{row.bocas ?? "–"}</Td>
                  <Td>
                    {Array.isArray(row.litros_por_boca) &&
                    row.litros_por_boca.length
                      ? row.litros_por_boca.join(", ")
                      : "–"}
                  </Td>
                  <Td>
                    {row.capacidade_total != null
                      ? Number(row.capacidade_total).toLocaleString("pt-BR")
                      : "–"}
                  </Td>
                  <Td>
                    <Flex gap={2}>
                      <Button
                        size="xs"
                        leftIcon={<FaEdit />}
                        onClick={() => openEdit(row)}
                      >
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        leftIcon={<FaTrash />}
                        onClick={() => handleDelete(row.id)}
                      >
                        Excluir
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
        {!loading && lista.length === 0 && (
          <Center py={8} color="gray.500">
            Nenhum veículo cadastrado. Clique em &quot;Novo veículo&quot; para
            cadastrar.
          </Center>
        )}
      </TableContainer>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingId ? "Editar veículo" : "Novo veículo"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex direction="column" gap={4}>
              <FormControl isRequired>
                <FormLabel>Placa</FormLabel>
                <Input
                  value={form.placa}
                  onChange={(e) => setForm({ ...form, placa: e.target.value })}
                  placeholder="Ex: ABC1D23"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Tipo</FormLabel>
                <Select
                  value={form.tipo}
                  onChange={(e) => setForm({ ...form, tipo: e.target.value })}
                  placeholder="Selecione o tipo"
                >
                  {TIPOS_VEICULO.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Bocas (quantidade de tanques)</FormLabel>
                <Input
                  type="number"
                  min={0}
                  value={form.bocas || ""}
                  onChange={(e) => setForm({ ...form, bocas: e.target.value })}
                  placeholder="Ex: 5"
                />
              </FormControl>
              {nBocas > 0 && (
                <Box>
                  <FormLabel mb={2}>Litros por boca (cada tanque)</FormLabel>
                  <Flex direction="column" gap={2}>
                    {form.litrosPorBoca.slice(0, nBocas).map((val, i) => (
                      <FormControl key={i}>
                        <FormLabel fontSize="sm">
                          Tanque {i + 1} (litros)
                        </FormLabel>
                        <Input
                          type="number"
                          min={0}
                          step={1}
                          value={val}
                          onChange={(e) =>
                            setLitrosPorBocaIndex(i, e.target.value)
                          }
                          placeholder="0"
                        />
                      </FormControl>
                    ))}
                  </Flex>
                </Box>
              )}
              <FormControl>
                <FormLabel>Capacidade total (litros)</FormLabel>
                <Input
                  value={capacidadeTotal.toLocaleString("pt-BR")}
                  readOnly
                  bg="gray.50"
                  isReadOnly
                />
              </FormControl>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button
              bg="#007abe"
              color="white"
              onClick={handleSave}
              isLoading={saving}
              _hover={{ bg: "#0066a3" }}
            >
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
